import {
  Typography,
  Divider,
  Grid,
  Drawer,
  IconButton,
  Box,
} from '@material-ui/core';
import React, { memo, useCallback, useEffect, useMemo } from "react";
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useStyles } from "./resumo-pedido-styles";
import { MeusPedidosIcon } from "utils";
import { localeString } from "utils/locale-string";
import { usePedidos } from "services/providers/pedidos-provider";
import { Carregando } from "components";
import { isEmpty } from "lodash";
import { PedidoProdutoModel } from "api/cardapio/models/pedido/pedido";
import { EnumTpProduto } from 'api/cardapio/models/produto/mov-simples-produto-model';
import { useThemeQueries } from 'components/dialogs/dialog-adicionar-produto-com-subitem/util-styles';
import { ProdutoPedidoCard } from './components/item-mesa-card/item-mesa-card';
import { EnumModals, useModalStore } from 'services/store/modal-store';
import { getIDFromUrl } from 'utils/get-id-url';

export interface CardapioResumoPedidoProps {
  aberto: boolean;
  codigoComanda?: string;
}

export const ResumoPedidoModal = memo(({
  aberto,
  codigoComanda,
  ...props
}: CardapioResumoPedidoProps) => {
  const classes = useStyles();
  const { theme, isMobile } = useThemeQueries();
  const variant = 'temporary';
  const anchor = isMobile ? 'bottom' : 'right';
  const openCloseModal = useModalStore(state => state.openCloseModal)
  const mesaId = getIDFromUrl('mesaId')

  const { pedidoAtual, getPedido, carregandoGetPedido } = usePedidos()

  useEffect(() => {
    if (aberto) {
      getPedido(codigoComanda);
    }
  }, [getPedido, aberto, codigoComanda])

  const precoFilter = (item: PedidoProdutoModel) => (item.status.codigo === 1 || item.status.codigo === 4) && item.indFin && item.tpProduto.codigo !== EnumTpProduto.Combo

  //Total dos itens da mesa
  const total = useCallback(() => {
    return localeString(pedidoAtual?.produtos.filter(precoFilter).reduce((a, b) => a + b.valorTotal, 0) ?? 0);
  }, [pedidoAtual?.produtos]);

  const totalDesconto = useCallback(() => {
    return localeString(pedidoAtual?.produtos.filter(precoFilter).reduce((a, b) => a + b.valorTotalDesconto, 0) || 0);
  }, [pedidoAtual?.produtos]);

  const totalTaxaServico = useMemo(() =>
    localeString(pedidoAtual?.produtos.filter(precoFilter).reduce((a, b) => a + b.valorTotalServico, 0) || 0)
    , [pedidoAtual?.produtos])

  const colorLogo = useMemo(() => {
    return theme.palette.type === 'light' ? theme.palette.primary.main : theme.palette.text.primary;
  }, [theme.palette.primary.main, theme.palette.text.primary, theme.palette.type]);



  const pedidoAgrupado = useMemo(() => {
    return pedidoAtual?.produtos.filter(x => !x.produtoPai).map((x) => ({
      ...x,
      subItens: (pedidoAtual?.produtos ?? []).filter(y => y.groupId === x.codigoReferencia && x.produtoIdReferencia !== y.produtoIdReferencia)
    }))
  }, [pedidoAtual?.produtos])

  return (
    <>
      <Drawer
        variant={variant}
        anchor={anchor}
        open={aberto}
        classes={{ paper: classes.drawer }}
        onClose={() => openCloseModal(EnumModals.ResumoPedido, false)}
      >

        <Carregando
          className={classes.carregando}
          carregando={carregandoGetPedido}
          modelo="absolute"
          titulo={'Carregando pedido'}
          mensagem={'Aguarde enquanto carregamos seu pedido.'}
        ></Carregando>

        <div className={classes.header}>
          <IconButton
            onClick={() => openCloseModal(EnumModals.ResumoPedido, false)}
            style={{ marginBottom: '-4px' }}
          >
            <ArrowBackIosIcon
              style={{ paddingLeft: '8px', fontSize: '32px', color: colorLogo }}
            />
          </IconButton>
          <div style={{ display: "flex", justifyContent: 'center', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: isMobile ? '8px' : '8px', display: 'flex' }}>
                <MeusPedidosIcon tipo='BUTTON' fill={colorLogo} />
              </div>
              <label htmlFor="MeusPedidos" style={{ fontWeight: 'bold', fontSize: '20px', fontStyle: 'revert' }}>MINHA CONTA</label>
            </div>
          </div>
        </div>

        <Divider style={{ marginTop: '1rem' }} />
        {!carregandoGetPedido && isEmpty(pedidoAtual) && isEmpty(pedidoAtual?.produtos) && (
          <Box height={'100%'} width="100%" display={"flex"} textAlign={"center"} alignItems={"center"} justifyContent={"center"}>
            <Typography variant="h5">
              <Box fontWeight={600}>
                Nenhum produto encontrado, faça um novo pedido.
              </Box>
            </Typography>
          </Box>
        )}
        {!carregandoGetPedido && (pedidoAtual?.produtos ?? []).length > 0 && (
          <>
            {(mesaId !== undefined &&
              <Grid container>
                <Grid item xs={6}>
                  <Typography color="textPrimary" className={classes.mesa}>
                    {`Mesa: ${pedidoAtual?.mesaCodigo ?? ''}`}
                  </Typography>
                </Grid>
                {!isEmpty(pedidoAtual?.codigoComanda) && (
                  <Grid item xs={6}>
                    <Typography color="textPrimary" className={classes.comanda}>
                      {`Comanda: ${pedidoAtual?.codigoComanda}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
            <Box mt={1}>
              <Divider />
            </Box>

            <div className={classes.scrollable}>
              {pedidoAgrupado?.filter(x => isEmpty(x.adicionalId)).map((item, index) => {
                return (
                  <ProdutoPedidoCard
                    item={item}
                    key={index}
                  />
                );
              })}
            </div>

            <div className={classes.footer}>
              <Divider />
              <Grid container justify="space-between" style={{ marginTop: '1rem' }}>
                <Grid item>
                  {totalDesconto() && (
                    <>
                      <Typography variant="body2" color="textSecondary">
                        Desconto
                      </Typography>
                      <Typography variant="h5" color="textPrimary" className={classes.textoTotais}>
                        {totalDesconto()}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item>
                  {totalTaxaServico && (
                    <>
                      <Typography variant="body2" color="textSecondary">
                        Taxa de serviço
                      </Typography>
                      <Typography variant="h5" color="textPrimary" className={classes.textoTotais}>
                        {totalTaxaServico}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="textSecondary">
                    Total
                  </Typography>

                  <Typography variant="h5" color="textPrimary" style={{ fontWeight: 700, fontSize: 26, color: theme.palette.primary.main }} className={classes.textoTotais}>
                    {total()}
                  </Typography>
                </Grid>
              </Grid>
              {/* {false && (
                <Grid container justify="center" className={classes.grdBtns}>
                  <Button
                    variant="contained"
                    size="large"
                    className={classes.btnFooter}
                    onClick={handleClickOpen}
                  >
                    Fechar a conta
                  </Button>
                  <div>
                    <Dialog
                      open={open}
                      keepMounted
                      onClose={quandoFechado}
                      aria-describedby="alert-dialog-slide-description"
                    >
                      <DialogTitle>{"Finalizar Conta"}</DialogTitle>
                      <DialogContent>
                        <DialogContentText id="alert-dialog-slide-description">
                          Você deseja chamar o garçom para finalizar a sua conta?
                        </DialogContentText>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleClose}>Sim</Button>
                      </DialogActions>
                    </Dialog>
                  </div>
                  <Button
                    variant="contained"
                    size="large"
                    className={classNames(classes.buttonPedir, classes.btnFooter)}
                  >
                    Chamar Garçom
                  </Button>
                </Grid>
              )} */}
            </div>
          </>
        )}
      </Drawer >
    </>
  );
});

ResumoPedidoModal.displayName = 'ResumoPedidoModal';
